import { useEffect, useRef, useState } from 'react';

/**
 * Measure an element's rendered (content-box) width via ResizeObserver.
 *
 * Returns `[ref, width]`. Attach `ref` to the element whose width should
 * drive geometry; `width` is `0` until the first measurement lands, so
 * width-dependent renderers should gate on `width > 0` (and reserve their
 * height on the ref'd element) to avoid drawing at a wrong scale for a
 * frame.
 *
 * Used by the trend-chart renderers (DefaultAreaChart, LuceTrendChart) so
 * each SVG can draw at 1 unit = 1 pixel — `viewBox` width === measured
 * width — instead of stretching a fixed 600-unit viewBox to fill via
 * `preserveAspectRatio="none"`, which smeared strokes + text and clipped
 * the last axis label on wide containers (e.g. the full-width Reports
 * card).
 *
 * The ref'd element should carry no horizontal padding/border so the
 * seed (getBoundingClientRect) and the observer (contentRect) agree.
 */
export function useMeasuredWidth<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Seed from current layout so the chart paints on the next frame
    // rather than waiting for a resize event that may never come.
    const seed = el.getBoundingClientRect().width;
    if (seed > 0) setWidth(seed);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = entry.contentRect.width;
      // Ignore sub-pixel jitter so we don't re-render (and re-animate the
      // line draw) on imperceptible layout churn.
      if (w > 0) setWidth((prev) => (Math.abs(prev - w) > 0.5 ? w : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width] as const;
}
